/**
 * ModernWash 운영 (/modernwash) — 레거시 "미러" 전용 읽기 화면.
 *
 * 절대 경계(과제 계약):
 *   - 미러 API(`api/modernwashMirror.js`)만 호출한다. 라이브 어댑터(`owner-console-v2/api/modernwash.js`)를
 *     절대 부르지 않고, 네이티브(모구) KPI 와도 절대 섞지 않는다.
 *   - "활성 구독 / 실제 세차 / 운영 매출 요약" 은 각각 라벨을 달고 **합산하지 않는다.**
 *   - 실 세차(actual_wash) 와 인식 오류/중복/미인식 이벤트는 별도 카운트다(실세차에 더하지 않는다).
 *   - 동기화 전/원천 없음/실패/지연을 구분하고, 단지 오래됐거나 없다는 이유로 **0 이라 단정하지 않는다.**
 *   - master 는 매장 선택, manager 는 서버가 본인 매장으로 강제(셀렉터 숨김). PII(번호판/이름)는 허용 스코프 내에서만.
 *
 * 백엔드 계약: backend/src/modernwash-legacy-mirror/*  (dto/controller/service)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import QueryError from '../components/QueryError';
import { getAdminRole, getAdminStoreCode } from '../utils/auth';
import { statusChipSx } from '../styles/uiTokens';
import {
  formatWon,
  formatNumber,
  formatDateTime,
} from '../owner-console-v2/modules/carwash/modernwash/format';
import {
  getMirrorMembers,
  getMirrorSummary,
  getMirrorPayments,
  getMirrorSubscriptions,
  getMirrorWash,
  getMirrorTaxonomy,
  getMirrorVehicles,
} from '../api/modernwashMirror';
import MirrorFilterBar from './modernwash/MirrorFilterBar';
import FreshnessBanner from './modernwash/FreshnessBanner';
import useMirrorFilters from './modernwash/useMirrorFilters';
import {
  toBackendPeriod,
  periodLabel,
  scopeLabel,
  categoryLabel,
  categoriesForService,
  renderMetricValue,
  sectionStateMeta,
} from './modernwash/mirrorFormat';

const SERVICE = 'modernwash';

const surfaceSx = {
  borderRadius: '12px',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  backgroundColor: '#fff',
};

// taxonomy 로딩 전 폴백 라벨 (백엔드 taxonomy 가 단일 소스이지만 초기 렌더 방어).
const WASH_FALLBACK = {
  actual_wash: { label: '실 세차', counted: true },
  not_recognized: { label: '미인식', counted: false },
  duplicate_recognition: { label: '중복 인식', counted: false },
  audit_only: { label: '감사 로그', counted: false },
  unknown: { label: '미분류', counted: false },
};

const SUBSCRIPTION_PRODUCT_LABELS = {
  all_in_one_subscription: '올인원 세차 구독',
  daily_subscription: '데일리 세차 구독',
};

const SUBSCRIPTION_STATUS_LABELS = {
  active: '정상 이용 중',
  cancellation_requested: '해지 요청',
};

const subscriptionProductLabel = (value) =>
  SUBSCRIPTION_PRODUCT_LABELS[value] || value || '-';

const subscriptionStatusLabel = (value) =>
  SUBSCRIPTION_STATUS_LABELS[value] || value || '-';

const vehiclePlateLabel = (plates) => {
  if (!Array.isArray(plates) || plates.length === 0) return '-';
  if (plates.length === 1) return plates[0];
  return `${plates[0]} 외 ${plates.length - 1}대`;
};

// ── 작은 표시 컴포넌트 ─────────────────────────────────────────
const SurfaceCard = ({ title, subtitle, action, children, sx }) => (
  <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 2, md: 3 }, ...sx }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: 16, md: 19 }, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

/** KPI 카드 — 절대 0 으로 단정하지 않는다(renderMetricValue 규칙). */
const MirrorStat = ({ icon, color, label, scope, value, sectionState, unit }) => {
  const { text, placeholder, badge } = renderMetricValue(value, sectionState, unit);
  return (
    <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2.25 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color,
            backgroundColor: alpha(color, 0.12),
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
      </Box>
      <Typography
        sx={{
          fontSize: placeholder ? 18 : { xs: 24, md: 28 },
          fontWeight: 800,
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          color: placeholder ? 'text.secondary' : 'text.primary',
        }}
        noWrap
      >
        {text}
      </Typography>
      {badge?.note ? (
        <Box sx={{ mt: 0.75 }}>
          <Box component="span" sx={statusChipSx(badge.tone)}>
            {badge.note}
          </Box>
        </Box>
      ) : null}
      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }} noWrap>
        {scope}
      </Typography>
    </Paper>
  );
};

const ModernWashPage = () => {
  const navigate = useNavigate();
  const role = getAdminRole();
  const isMaster = role === 'master';
  const storeCode = getAdminStoreCode();

  const [filters, updateFilters] = useMirrorFilters({ defaultService: SERVICE });
  const periodState = useMemo(
    () => ({ period: filters.period, month: filters.month, start: filters.start, end: filters.end }),
    [filters.period, filters.month, filters.start, filters.end],
  );
  const backendPeriod = useMemo(() => toBackendPeriod(periodState), [periodState]);
  const storeParam = isMaster ? filters.storeId || undefined : undefined; // manager 는 storeId 를 보내지 않는다

  // ── 카테고리 taxonomy (백엔드 단일 소스) ──
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    let alive = true;
    getMirrorTaxonomy(SERVICE)
      .then((t) => alive && setTaxonomy(t))
      .catch(() => alive && setTaxonomy(null));
    return () => {
      alive = false;
    };
  }, []);

  // ── master 매장 목록 (스코프 선택용 — KPI 소스 아님) ──
  const [stores, setStores] = useState([]);
  useEffect(() => {
    if (!isMaster) return undefined;
    let alive = true;
    api
      .get('/stores')
      .then((res) => alive && setStores(res.data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isMaster]);

  // ── 기간 스코프 핵심 데이터 (summary/payments/subscriptions) ──
  // summary 의 freshness 를 배너와 KPI의 단일 기준으로 사용한다. 별도 freshness 요청은
  // 동기화 포인터 승격 순간에 다른 active snapshot 을 읽을 수 있어 사용하지 않는다.
  const [core, setCore] = useState({ loading: true, error: null });
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState(null);
  const [subs, setSubs] = useState(null);
  const [subscriptionSearchInput, setSubscriptionSearchInput] = useState('');
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionPage, setSubscriptionPage] = useState(1);

  const loadCore = useCallback(() => {
    let alive = true;
    setCore({ loading: true, error: null });
    const base = { service: SERVICE, storeId: storeParam, ...backendPeriod };
    Promise.allSettled([
      getMirrorSummary(base),
      getMirrorPayments(base),
      getMirrorSubscriptions({
        service: SERVICE,
        storeId: storeParam,
        search: subscriptionSearch || undefined,
        status: subscriptionStatus || undefined,
        page: subscriptionPage,
        limit: 25,
      }),
    ]).then((results) => {
      if (!alive) return;
      const [s, p, sub] = results;
      if (s.status === 'fulfilled') setSummary(s.value);
      if (p.status === 'fulfilled') setPayments(p.value);
      if (sub.status === 'fulfilled') setSubs(sub.value);
      const firstErr = results.find((r) => r.status === 'rejected');
      setCore({ loading: false, error: firstErr ? firstErr.reason : null });
    });
    return () => {
      alive = false;
    };
  }, [backendPeriod, storeParam, subscriptionSearch, subscriptionStatus, subscriptionPage]);
  useEffect(() => loadCore(), [loadCore]);

  // 매장 스코프가 바뀌면 이전 검색 결과 페이지를 그대로 유지하지 않는다.
  useEffect(() => {
    setSubscriptionPage(1);
  }, [storeParam]);

  // ── 회원/차량 미러 목록 (이미 적재된 legacy_mw_*만 조회; V1 라이브 재호출 없음) ──
  const [directorySearchInput, setDirectorySearchInput] = useState('');
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directory, setDirectory] = useState({ loading: true, error: null, members: null, vehicles: null });

  const loadDirectory = useCallback(() => {
    let alive = true;
    setDirectory((previous) => ({ ...previous, loading: true, error: null }));
    const base = {
      service: SERVICE,
      storeId: storeParam,
      search: directorySearch || undefined,
      page: directoryPage,
      limit: 25,
    };
    Promise.allSettled([getMirrorMembers(base), getMirrorVehicles(base)]).then((results) => {
      if (!alive) return;
      const [members, vehicles] = results;
      const firstErr = results.find((result) => result.status === 'rejected');
      setDirectory({
        loading: false,
        error: firstErr ? firstErr.reason : null,
        members: members.status === 'fulfilled' ? members.value : null,
        vehicles: vehicles.status === 'fulfilled' ? vehicles.value : null,
      });
    });
    return () => {
      alive = false;
    };
  }, [storeParam, directorySearch, directoryPage]);
  useEffect(() => loadDirectory(), [loadDirectory]);

  // ── 세차 기록 목록 (카테고리 필터 + 일자 드릴다운) ──
  const [selectedDay, setSelectedDay] = useState('');
  // 기간/매장이 바뀌면 일자 드릴다운은 초기화한다.
  useEffect(() => {
    setSelectedDay('');
  }, [filters.period, filters.month, filters.start, filters.end, filters.storeId]);

  const washPeriod = selectedDay
    ? { period: 'custom', start: selectedDay, end: selectedDay }
    : backendPeriod;
  const washPeriodKey = `${washPeriod.period}|${washPeriod.start || ''}|${washPeriod.end || ''}`;

  const [wash, setWash] = useState(null);
  const [washLoading, setWashLoading] = useState(true);
  const [washError, setWashError] = useState(null);

  const loadWash = useCallback(() => {
    let alive = true;
    setWashLoading(true);
    setWashError(null);
    getMirrorWash({
      service: SERVICE,
      storeId: storeParam,
      category: filters.category || undefined,
      ...washPeriod,
      limit: 100,
    })
      .then((w) => alive && setWash(w))
      .catch((e) => alive && setWashError(e))
      .finally(() => alive && setWashLoading(false));
    return () => {
      alive = false;
    };
    // washPeriod 는 매 렌더 새 객체 → 원시 키로만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeParam, filters.category, washPeriodKey]);
  useEffect(() => loadWash(), [loadWash]);

  const refreshAll = () => {
    loadCore();
    loadDirectory();
    loadWash();
  };

  const submitDirectorySearch = (event) => {
    event.preventDefault();
    setDirectoryPage(1);
    setDirectorySearch(directorySearchInput.trim());
  };

  const submitSubscriptionSearch = (event) => {
    event.preventDefault();
    setSubscriptionPage(1);
    setSubscriptionSearch(subscriptionSearchInput.trim());
  };

  // ── 파생 값 ──
  const scope = summary?.scope || null;
  const reason = summary?.reason || null;
  const freshnessEnv = summary?.freshness || null;
  const data = summary?.data || null;
  const sections = data?.sections || {};
  const categoryText = categoryLabel(taxonomy, SERVICE, filters.category);
  const makeScope = (catOverride) =>
    scopeLabel({ service: SERVICE, categoryText: catOverride ?? categoryText, periodState });
  const basisLabel = periodLabel(periodState);

  const washCats = categoriesForService(taxonomy, SERVICE);
  const breakdown = data?.washBreakdown || null;
  const washRecordsState = sections.washRecords || 'never_synced';

  // 일자 드릴다운 옵션 — 결제 일별 요약의 businessDate 를 사용.
  const dayOptions = useMemo(() => {
    const items = payments?.data?.items || [];
    return items.map((it) => it.businessDate).filter(Boolean);
  }, [payments]);

  const managerStoreName = scope?.storeName || (storeCode ? `내 매장(${storeCode})` : '내 매장');

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, md: 3 } }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/')} aria-label="대시보드로">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 30 }, fontWeight: 800, letterSpacing: '-0.03em' }}>
            ModernWash 운영
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            V1 레거시 미러 기반 운영 현황 (네이티브 모구 지표와 분리)
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isMaster ? 'ModernWash 전체 조회 (master)' : '본인 매장 고정 (manager)'}
            variant="outlined"
            sx={{ height: 30, borderRadius: 999, fontWeight: 700 }}
          />
          <IconButton size="small" onClick={refreshAll} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* 신선도 배너 (읽기 전용 미러 + 마지막 성공 시각 + 데이터 기준) */}
      <Box sx={{ mb: 3 }}>
        <FreshnessBanner freshness={freshnessEnv} reason={reason} basisLabel={basisLabel} />
      </Box>

      {/* 필터 바 (service 는 modernwash 고정 → 토글 숨김) */}
      <MirrorFilterBar
        value={{ ...filters, service: SERVICE }}
        onChange={updateFilters}
        taxonomy={taxonomy}
        stores={stores}
        role={role}
        managerStoreName={managerStoreName}
        showService={false}
      />

      {core.error ? (
        <QueryError error={core.error} onRetry={refreshAll} context="ModernWash 운영 현황" />
      ) : null}
      {core.loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {/* KPI — 각각 별도 라벨, 절대 합산/0단정 금지 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', xl: 'repeat(4, minmax(0,1fr))' },
          gap: 2,
          mb: 3,
        }}
      >
        <MirrorStat
          icon={<SubscriptionsIcon />}
          color="#16A34A"
          label="활성 구독"
          scope={makeScope()}
          value={data?.subscriptionCount}
          sectionState={sections.subscriptions || 'never_synced'}
          unit="건"
        />
        <MirrorStat
          icon={<LocalCarWashIcon />}
          color="#7C3AED"
          label="실제 세차 (actual_wash)"
          scope={makeScope()}
          value={data?.actualWashCount}
          sectionState={washRecordsState}
          unit="건"
        />
        <MirrorStat
          icon={<PeopleIcon />}
          color="#2563EB"
          label="회원 수"
          scope={makeScope()}
          value={data?.memberCount}
          sectionState={sections.members || 'never_synced'}
          unit="명"
        />
        <MirrorStat
          icon={<PaymentIcon />}
          color="#EA580C"
          label="운영 매출 참고치 (합산 제외)"
          scope={makeScope()}
          value={data?.paymentReference?.grossAmount}
          sectionState={sections.paymentDailySummaries || 'never_synced'}
          unit="원"
        />
      </Box>

      {/* 회원/차량 목록 — 이미 성공한 미러 스냅샷만. 이메일·전화·결제식별자는 표시하지 않는다. */}
      <Box sx={{ mb: 3 }}>
        <SectionDirectory
          directory={directory}
          searchInput={directorySearchInput}
          onSearchInputChange={setDirectorySearchInput}
          onSubmitSearch={submitDirectorySearch}
          onRetry={loadDirectory}
          page={directoryPage}
          onPageChange={setDirectoryPage}
        />
      </Box>

      {/* 실제 세차 vs 인식 이벤트 — 절대 합산하지 않는다 */}
      <Box sx={{ mb: 3 }}>
        <SurfaceCard
          title="세차 인식 분류"
          subtitle="실 세차(actual_wash)만 실제 세차로 집계합니다. 미인식·중복·감사·미분류는 별도 이벤트로, 실세차에 더하지 않습니다."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 1.5,
            }}
          >
            {['actual_wash', 'not_recognized', 'duplicate_recognition', 'audit_only', 'unknown'].map((key) => {
              const meta =
                washCats.find((c) => c.key === key) ||
                { label: WASH_FALLBACK[key].label, countedInActualWash: WASH_FALLBACK[key].counted };
              const counted = meta.countedInActualWash;
              const rawVal = breakdown ? breakdown[key] : undefined;
              const cell = renderMetricValue(rawVal, washRecordsState, '건');
              return (
                <Box
                  key={key}
                  sx={{
                    border: '1px solid',
                    borderColor: counted ? 'rgba(124,58,237,0.35)' : 'divider',
                    borderRadius: '10px',
                    p: 1.5,
                    backgroundColor: counted ? alpha('#7C3AED', 0.05) : '#FCFCFD',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{meta.label}</Typography>
                    <Box component="span" sx={statusChipSx(counted ? 'purple' : 'neutral')}>
                      {counted ? '실세차' : '실세차 아님'}
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontSize: cell.placeholder ? 14 : 22, fontWeight: 800, color: cell.placeholder ? 'text.secondary' : 'text.primary' }}
                    noWrap
                  >
                    {cell.text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </SurfaceCard>
      </Box>

      {/* 활성 구독 — 검색/상태 필터도 서버가 매장 스코프를 강제한다. */}
      <Box sx={{ mb: 3 }}>
        <SectionSubscriptions
          subs={subs}
          scopeLabelText={makeScope()}
          searchInput={subscriptionSearchInput}
          appliedSearch={subscriptionSearch}
          onSearchInputChange={setSubscriptionSearchInput}
          onSubmitSearch={submitSubscriptionSearch}
          status={subscriptionStatus}
          onStatusChange={(next) => {
            setSubscriptionPage(1);
            setSubscriptionStatus(next);
          }}
          page={subscriptionPage}
          onPageChange={setSubscriptionPage}
        />
      </Box>

      {/* 운영 매출 요약 (참고치 — 재무 원장 아님) */}
      <Box sx={{ mb: 3 }}>
        <SectionPayments payments={payments} basisLabel={basisLabel} />
      </Box>

      {/* 세차 기록 목록 — 카테고리 필터 + 일자 드릴다운 */}
      <Box sx={{ mb: 3 }}>
        <SectionWashRecords
          wash={wash}
          loading={washLoading}
          error={washError}
          onRetry={loadWash}
          categoryText={categoryText}
          dayOptions={dayOptions}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
        />
      </Box>
    </Box>
  );
};

// ── 회원/차량 미러 목록 ─────────────────────────────────────────
const SectionDirectory = ({
  directory,
  searchInput,
  onSearchInputChange,
  onSubmitSearch,
  onRetry,
  page,
  onPageChange,
}) => {
  const members = directory.members?.data || null;
  const vehicles = directory.vehicles?.data || null;
  const memberState = members?.dataState || 'never_synced';
  const vehicleState = vehicles?.dataState || 'never_synced';
  const totalPages = Math.max(1, Math.ceil(Math.max(members?.total || 0, vehicles?.total || 0) / 25));

  return (
    <SurfaceCard
      title="V1 회원 · 차량 목록"
      subtitle="매일 적재된 ModernWash V1 미러입니다. 표시명·멤버십 상태·번호판만 보이며 이메일·전화·결제 정보는 노출하지 않습니다."
      action={
        <Box component="form" onSubmit={onSubmitSearch} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Box
            component="input"
            aria-label="회원 또는 차량 검색"
            value={searchInput}
            onChange={(event) => onSearchInputChange(event.target.value)}
            placeholder="이름 또는 번호판 검색"
            sx={{ width: 180, height: 34, px: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: '8px', fontSize: 13 }}
          />
          <Box component="button" type="submit" sx={{ height: 34, px: 1.25, border: 0, borderRadius: '8px', color: '#fff', backgroundColor: 'primary.main', fontWeight: 700, cursor: 'pointer' }}>
            검색
          </Box>
        </Box>
      }
    >
      {directory.error ? (
        <QueryError error={directory.error} onRetry={onRetry} context="V1 회원·차량 목록" />
      ) : directory.loading ? (
        <LinearProgress />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <DirectoryTable
              title="회원"
              state={memberState}
              total={members?.total}
              headers={['표시명', '멤버십 상태', '차량']}
              rows={(members?.items || []).map((row) => [row.displayName || '-', row.membershipStatus || '-', `${formatNumber(row.vehicleCount)}대`])}
            />
            <DirectoryTable
              title="차량"
              state={vehicleState}
              total={vehicles?.total}
              headers={['번호판', '회원']}
              rows={(vehicles?.items || []).map((row) => [row.licensePlate || '-', row.memberDisplayName || '-'])}
              monoFirst
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Box component="button" type="button" aria-label="이전 목록 페이지" disabled={page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))} sx={pageButtonSx(page <= 1)}>
              이전
            </Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{page} / {totalPages}</Typography>
            <Box component="button" type="button" aria-label="다음 목록 페이지" disabled={page >= totalPages} onClick={() => onPageChange(Math.min(totalPages, page + 1))} sx={pageButtonSx(page >= totalPages)}>
              다음
            </Box>
          </Box>
        </>
      )}
    </SurfaceCard>
  );
};

const pageButtonSx = (disabled) => ({
  height: 30,
  px: 1.25,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '7px',
  color: disabled ? 'text.disabled' : 'text.primary',
  backgroundColor: '#fff',
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const DirectoryTable = ({ title, state, total, headers, rows, monoFirst = false }) => {
  const meta = sectionStateMeta(state);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{title} {total == null ? '' : `(${formatNumber(total)})`}</Typography>
        <Box component="span" sx={statusChipSx(meta.tone)}>{meta.label || '정상'}</Box>
      </Box>
      {state === 'never_synced' ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>동기화 전 · 준비중</Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{state === 'no_source_data' ? '원천 데이터 없음' : '검색 결과가 없습니다.'}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow>{headers.map((header) => <TableCell key={header}>{header}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {rows.map((cells, index) => (
                <TableRow key={`${title}-${index}`} hover>
                  {cells.map((cell, cellIndex) => <TableCell key={cellIndex} sx={monoFirst && cellIndex === 0 ? { fontFamily: 'monospace' } : undefined}>{cell}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── 활성 구독 섹션 ─────────────────────────────────────────────
const SectionSubscriptions = ({
  subs,
  scopeLabelText,
  searchInput,
  appliedSearch,
  onSearchInputChange,
  onSubmitSearch,
  status,
  onStatusChange,
  page,
  onPageChange,
}) => {
  const state = subs?.data?.dataState || 'never_synced';
  const items = subs?.data?.items || [];
  const meta = sectionStateMeta(state);
  const isReady = state === 'never_synced';
  const total = subs?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / (subs?.data?.limit || 25)));
  const hasQuery = Boolean(appliedSearch || status);

  return (
    <SurfaceCard
      title={`활성 구독${state === 'never_synced' ? '' : ` (${formatNumber(total)}건)`}`}
      subtitle={`${scopeLabelText} · 테스트/QA·무제한 권한은 제외 · 구독자·차량 연결은 허용된 매장 원천 기준`}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Box component="select" aria-label="활성 구독 상태" value={status} onChange={(event) => onStatusChange(event.target.value)} sx={{ height: 34, px: 1, border: '1px solid', borderColor: 'divider', borderRadius: '8px', backgroundColor: '#fff', fontSize: 12.5, fontWeight: 700 }}>
            <option value="">전체 상태</option>
            <option value="active">정상 이용 중</option>
            <option value="cancellation_requested">해지 요청</option>
          </Box>
          <Box component="form" onSubmit={onSubmitSearch} sx={{ display: 'flex', gap: 0.5 }}>
            <Box component="input" aria-label="활성 구독 검색" value={searchInput} onChange={(event) => onSearchInputChange(event.target.value)} placeholder="구독자·차량번호" sx={{ width: 150, height: 34, px: 1, border: '1px solid', borderColor: 'divider', borderRadius: '8px', fontSize: 12.5 }} />
            <Box component="button" type="submit" sx={{ height: 34, px: 1.1, border: 0, borderRadius: '8px', color: '#fff', backgroundColor: 'primary.main', fontWeight: 700, cursor: 'pointer' }}>검색</Box>
          </Box>
          <Box component="span" sx={statusChipSx(meta.tone)}>{meta.label || '정상'}</Box>
        </Box>
      }
    >
      {isReady || items.length === 0 ? (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: '10px',
            backgroundColor: '#FCFCFD',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>
            동기화 전 · 원천 준비중
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
            구독 원천 동기화는 아직 준비 중입니다. (숫자 0 이 아니라 미확보 상태입니다.)
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {hasQuery ? '검색·상태 조건에 맞는 구독이 없습니다.' : state === 'no_source_data' ? '원천 데이터 없음 (활성 구독 0건)' : '표시할 활성 구독이 없습니다.'}
          </Typography>
        </Box>
      ) : (
        <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>구독자</TableCell>
                <TableCell>차량번호</TableCell>
                <TableCell>상품</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="right">시작</TableCell>
                <TableCell align="right">만료</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.legacyId} hover>
                  <TableCell>{it.subscriberDisplayName || '원천 회원 연결 정보 없음'}</TableCell>
                  <TableCell>
                    {it.connectionNote ? (
                      <Box component="span" sx={statusChipSx('neutral')}>{it.connectionNote}</Box>
                    ) : vehiclePlateLabel(it.vehicleLicensePlates)}
                  </TableCell>
                  <TableCell>{subscriptionProductLabel(it.productName)}</TableCell>
                  <TableCell>{subscriptionStatusLabel(it.status)}</TableCell>
                  <TableCell align="right">{it.startedAt ? formatDateTime(it.startedAt) : '-'}</TableCell>
                  <TableCell align="right">{it.expiresAt ? formatDateTime(it.expiresAt) : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Box component="button" type="button" aria-label="이전 구독 페이지" disabled={page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))} sx={pageButtonSx(page <= 1)}>이전</Box>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{page} / {totalPages}</Typography>
          <Box component="button" type="button" aria-label="다음 구독 페이지" disabled={page >= totalPages} onClick={() => onPageChange(Math.min(totalPages, page + 1))} sx={pageButtonSx(page >= totalPages)}>다음</Box>
        </Box>
        </>
      )}
    </SurfaceCard>
  );
};

// ── 운영 매출 요약 섹션 (참고치) ───────────────────────────────
const SectionPayments = ({ payments, basisLabel }) => {
  const state = payments?.data?.dataState || 'never_synced';
  const items = payments?.data?.items || [];
  const meta = sectionStateMeta(state);
  const totalGross = payments?.data?.totalGrossAmount;
  const totalCount = payments?.data?.totalPaymentCount;
  const isPlaceholder = state === 'never_synced';

  return (
    <SurfaceCard
      title="운영 매출 요약"
      subtitle={`V1 매출 관리 기준 양수 결제 합계 · Mogoo 네이티브 매출과 합산하지 않음 · 재무 원장/순매출 아님 · 데이터 기준 ${basisLabel}`}
      action={
        <Box component="span" sx={statusChipSx(meta.tone)}>
          {meta.label || '정상'}
        </Box>
      }
    >
      <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.25, borderRadius: '8px', '& .MuiAlert-message': { fontSize: 12.5, fontWeight: 600 } }}>
        이 값은 ModernWash V1 운영 참고치입니다. Mogoo 자체 매출·재무 원장과 합산하거나 정산 기준으로 사용하지 마세요.
      </Alert>
      {isPlaceholder ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>동기화 전 · 준비중</Typography>
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {state === 'no_source_data' ? '원천 데이터 없음 (기간 내 결제 요약 0건)' : '표시할 결제 요약이 없습니다.'}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>양수 결제 합계(참고)</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{formatWon(totalGross)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>합계 건수</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{formatNumber(totalCount)}건</Typography>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>영업일</TableCell>
                  <TableCell align="center">건수</TableCell>
                  <TableCell align="right">결제액</TableCell>
                  <TableCell align="right">환불</TableCell>
                  <TableCell align="right">순액</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.businessDate} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.businessDate}</TableCell>
                    <TableCell align="center">{formatNumber(r.paymentCount)}</TableCell>
                    <TableCell align="right">{formatWon(r.grossAmount)}</TableCell>
                    <TableCell align="right">{r.refundAmount == null ? '-' : formatWon(r.refundAmount)}</TableCell>
                    <TableCell align="right">{r.netAmount == null ? '-' : formatWon(r.netAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </SurfaceCard>
  );
};

// ── 세차 기록 목록 섹션 (일자 드릴다운) ────────────────────────
const SectionWashRecords = ({
  wash,
  loading,
  error,
  onRetry,
  categoryText,
  dayOptions,
  selectedDay,
  onSelectDay,
}) => {
  const items = wash?.data?.items || [];
  const state = wash?.data?.dataState || 'never_synced';
  const meta = sectionStateMeta(state);

  const daySelect = (
    <Box
      component="select"
      aria-label="일자 드릴다운"
      value={selectedDay}
      onChange={(e) => onSelectDay(e.target.value)}
      sx={{
        height: 34,
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.16)',
        px: 1,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: 'inherit',
        backgroundColor: '#fff',
      }}
    >
      <option value="">전체 기간</option>
      {dayOptions.map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </Box>
  );

  return (
    <SurfaceCard
      title="세차 기록 목록"
      subtitle={`카테고리: ${categoryText} · 실 세차/이벤트 원본 로그 (허용된 매장 스코프 내에서만 번호판 표시)`}
      action={daySelect}
    >
      {error ? (
        <QueryError error={error} onRetry={onRetry} context="세차 기록" />
      ) : loading ? (
        <LinearProgress />
      ) : state === 'never_synced' ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>동기화 전 · 준비중</Typography>
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {state === 'no_source_data' ? '원천 데이터 없음 (기간 내 세차 기록 0건)' : '표시할 세차 기록이 없습니다.'}
          </Typography>
        </Box>
      ) : (
        <>
          {(state === 'stale' || state === 'failed_showing_last_success') && (
            <Box component="span" sx={{ ...statusChipSx(meta.tone), mb: 1.5, display: 'inline-flex' }}>
              {meta.label}
            </Box>
          )}
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>발생 시각</TableCell>
                  <TableCell>번호판</TableCell>
                  <TableCell>분류</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, i) => (
                  <TableRow key={`${it.licensePlate}-${it.occurredAt}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {it.occurredAt ? formatDateTime(it.occurredAt) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{it.licensePlate || '-'}</TableCell>
                    <TableCell>
                      {(WASH_FALLBACK[it.classification]?.label) || it.classification}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </SurfaceCard>
  );
};

export default ModernWashPage;
